import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { startWith } from 'rxjs';

import { FilterMethod } from '@core/models/data-visualization/data-visualization.state';
import { ICONS } from '@core/services/icons/icons.service';
import { setFilterOnAggregator } from '@core/store/data-visualization/data-visualization.actions';
import { selectAggregatorFilter } from '@core/store/data-visualization/data-visualization.selectors';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { ItemsDropdownComponent } from '@shared/components/dropdown/template/items-dropdown/items-dropdown.component';
import { TooltipComponent } from '@shared/components/tooltip/tooltip.component';
import { DropdownItem } from '@shared/models/dropdown-item.model';

import { ListComponent } from '@features/components/visualization/list/list/list.component';
import {
  List,
  ListViewState,
  Lists
} from '@features/components/visualization/list/models/list-visualization';

export const initialListViewState: ListViewState = {
  currentVisuId: '-',
  listNames: {},
  maxNbListsDisplayed: 5,
  listsAvailable: [],
  listsSelected: [],
  listsDisplayed: {},
  lists: {}
};

@Component({
  selector: 'app-list-view',
  imports: [
    ListComponent,
    CdkDropListGroup,
    DropdownComponent,
    ItemsDropdownComponent,
    TooltipComponent,
    TranslatePipe
  ],
  templateUrl: './list-view.component.html'
})
export class ListViewComponent implements AfterViewInit {
  protected readonly store = inject(Store);
  protected readonly translate = inject(TranslateService);
  protected readonly options$ = computed(() => this.getOptions());
  protected readonly lists$ = computed(() => this.getLists());
  protected readonly dropdownItems$ = computed(() => this.getListSelectionItems());
  protected isAddListDropdownOpen = signal<boolean>(false);
  private readonly aggregatorFilter$ = this.store.select(selectAggregatorFilter);
  private readonly aggregatorFilter = toSignal(this.aggregatorFilter$, {
    initialValue: { filteredAggregatorIds: [], filterMethod: FilterMethod.DISABLED }
  });

  availableLists = input<List[]>([]);
  addExternalMapButton = input<boolean>(false);
  allowEdit = input<boolean>(false);
  allowExport = input<boolean>(true);
  allowTaskDone = input<boolean>(false);
  canFilterData = input<boolean>(false);
  fullScreen = input<boolean>(false);
  instanceType = input<string>('input');

  taskDone = output();
  export = output();

  @ViewChild('list') protected listDiv!: ElementRef;

  protected mainClassList = computed(() => {
    const baseClass = 'bg-neutral-50 hover:bg-neutral-200 shrink-0 grow-0';
    return this.fullScreen() ? `${baseClass} w-0` : `${baseClass} w-5`;
  });
  protected showAll = computed(() => this.listViewState().listsAvailable.length === 0);

  private listViewState = signal<ListViewState>(initialListViewState);
  private scroller: NodeJS.Timeout | undefined;

  ngAfterViewInit() {
    if (this.availableLists().length > 0) {
      this.resetListStateWithCurrentFilters(this.availableLists());
    }

    if (this.canFilterData()) {
      this.aggregatorFilter$
        .pipe(startWith(this.aggregatorFilter()))
        .subscribe(() => this.resetListStateWithCurrentFilters(this.availableLists()));
    }
  }

  exportPrimaryAggregator($event: { primaryAggregatorId: number }) {
    console.log('Exporting primary aggregator with ID:', $event.primaryAggregatorId);
  }

  startScroll(jumpSize: number) {
    this.scroller = setInterval(
      () => (this.listDiv.nativeElement.scrollLeft += jumpSize),
      7
    );
  }

  stopScroll() {
    clearInterval(this.scroller);
  }

  private getOptions() {
    const { listsAvailable, listNames } = this.listViewState();

    return listsAvailable.map((id) => ({ id, name: listNames[id] }));
  }

  private resetListStateWithCurrentFilters(baseLists: List[]) {
    const listsAvailable: string[] = [];
    const listsSelected: string[] = [];
    const listsDisplayed: { [id: string]: boolean } = {};
    const lists: Lists = {};

    baseLists.forEach((list) => {
      lists[list.aggregatorId] = list;
      listsDisplayed[list.aggregatorId] = false;
    });

    const filteredAggregatorIds = this.aggregatorFilter().filteredAggregatorIds;
    baseLists.forEach((list) => {
      const id = list.aggregatorId;
      if (filteredAggregatorIds.includes(id)) {
        listsDisplayed[id] = true;
        listsSelected.push(id);
      } else {
        listsAvailable.push(id);
        listsDisplayed[id] = false;
      }
    });

    this.listViewState.update((state) => ({
      ...state,
      listsAvailable,
      listsSelected,
      listsDisplayed,
      lists
    }));
  }

  private getLists() {
    const { listsSelected, listsDisplayed, lists } = this.listViewState();

    return listsSelected
      .filter((id) => listsDisplayed[id])
      .map((id) => ({ id, list: lists[id] }));
  }

  protected selectList(id: string) {
    const { listsSelected, listsAvailable, listsDisplayed } = this.listViewState();

    const pos = listsAvailable.indexOf(id);
    if (pos >= 0) {
      const newListsAvailable = [...listsAvailable];
      newListsAvailable.splice(pos, 1);
      const newListsSelected = [...listsSelected, id];
      const newListsDisplayed = { ...listsDisplayed };
      newListsDisplayed[id] = true;

      if (this.canFilterData()) {
        this.store.dispatch(
          setFilterOnAggregator({
            filteredAggregatorIds: newListsSelected,
            filterMethod: FilterMethod.BY_AGGREGATOR
          })
        );
      }

      this.listViewState.update((state) => ({
        ...state,
        listsAvailable: newListsAvailable,
        listsSelected: newListsSelected,
        listsDisplayed: newListsDisplayed
      }));
      return;
    }
    return;
  }

  protected unselectList(id: string) {
    const { listsSelected, listsAvailable, listsDisplayed } = this.listViewState();

    const pos = listsSelected.indexOf(id);
    if (pos >= 0) {
      const newListsAvailable = [...listsAvailable, id];
      const newListsSelected = [...listsSelected];
      newListsSelected.splice(pos, 1);
      const newListsDisplayed = { ...listsDisplayed };
      newListsDisplayed[id] = false;

      if (this.canFilterData()) {
        this.store.dispatch(
          setFilterOnAggregator({
            filteredAggregatorIds: newListsSelected,
            filterMethod: FilterMethod.BY_AGGREGATOR
          })
        );
      }

      this.listViewState.update((state) => ({
        ...state,
        listsAvailable: newListsAvailable,
        listsSelected: newListsSelected,
        listsDisplayed: newListsDisplayed
      }));
      return;
    }
    return;
  }

  protected readonly ICONS = ICONS;

  toggleAddListDropdown(): void {
    if (this.dropdownItems$().length === 0) {
      return;
    }
    this.isAddListDropdownOpen.update((v) => !v);
  }

  onCloseAddListDropdown = (): void => {
    this.isAddListDropdownOpen.set(false);
  };

  getListSelectionItems(): DropdownItem[] {
    const { listsAvailable, listsSelected } = this.listViewState();

    return listsAvailable.map((id) => ({
      label: id,
      action: () => {
        if (listsSelected.includes(id)) {
          this.unselectList(id);
        } else {
          this.selectList(id);
        }
      }
    })) as DropdownItem[];
  }
}
